import { buildSocialLoginPath } from '@/utils/signupReferral';
/**
 * Authentication service for Flask API integration
 *
 * Features:
 * - Flask JWT token validation
 * - Token storage in localStorage
 * - User data fetching
 * - Credits management
 * - Login/logout flows
 * - Social login (Google, Naver, Kakao) via Flask OAuth
 */

import api from './api';
import type {
  User,
  GetCreditsResponse,
} from '@/types';
import { decodeJwtPayload, getImpersonatorId } from '@/utils/impersonationClaim';

const TOKEN_STORAGE_KEY = 'auth_token';
const USER_STORAGE_KEY = 'auth_user';
const VALIDATION_TIMESTAMP_KEY = 'auth_validated_at';
const VALIDATION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in ms
const REFRESH_THRESHOLD_MS = 2 * 24 * 60 * 60 * 1000; // 2 days in ms
const CLOCK_SKEW_BUFFER_MS = 30_000; // 30 seconds buffer for clock skew

// Custom event dispatched whenever the stored auth token mutates.
// Same-tab consumers (AdminModeBar, AppChrome) subscribe via useSyncExternalStore
// so the impersonation banner stays in sync with localStorage regardless of
// React state timing (bfcache restore, back-navigation, cross-component races).
export const AUTH_TOKEN_CHANGE_EVENT = 'auth:token-changed';

function dispatchTokenChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(AUTH_TOKEN_CHANGE_EVENT));
}

type AuthApiError = Error & {
  response?: {
    status?: number;
    data?: {
      message?: string;
      code?: string;
      error?: string;
    };
  };
};

// The JWT decoder lives in utils/impersonationClaim.ts (no imports) so api.ts can read the
// impersonator claim without importing this module; re-exported for Login.tsx and AuthContext.
export { decodeJwtPayload, getImpersonatorId };

/**
 * Check if JWT token is expired based on 'exp' claim
 * Returns true if token is valid (not expired), false if expired or invalid
 */
function isTokenValid(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return false;

  // exp is in seconds, Date.now() is in milliseconds
  // Add 60-second buffer to avoid edge cases
  return payload.exp * 1000 > Date.now() + 60000;
}

/**
 * Check if token should be refreshed (within 2-day window before expiration)
 * Returns true if token is valid but expires within REFRESH_THRESHOLD_MS
 */
function shouldRefreshToken(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;

  const expirationMs = payload.exp * 1000;
  const now = Date.now();

  // Token is already expired (with clock skew buffer)
  if (expirationMs < now - CLOCK_SKEW_BUFFER_MS) return false;

  // Token expires within threshold
  const timeUntilExpiry = expirationMs - now;
  return timeUntilExpiry > 0 && timeUntilExpiry < REFRESH_THRESHOLD_MS;
}

/**
 * Authentication Service
 */
export const authService = {
  /**
   * Store JWT token in localStorage
   * Clears validation cache and cached user when setting a NEW token
   */
  setToken(token: string): void {
    // Clear validation cache when setting a NEW token
    const existingToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const changed = existingToken !== token;
    if (changed) {
      this.clearValidationCache();
      localStorage.removeItem(USER_STORAGE_KEY);
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    if (changed) {
      dispatchTokenChange();
    }
  },

  /**
   * Get JWT token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  },

  /**
   * Remove JWT token from localStorage
   */
  clearToken(): void {
    const hadToken = localStorage.getItem(TOKEN_STORAGE_KEY) != null;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(VALIDATION_TIMESTAMP_KEY);
    if (hadToken) {
      dispatchTokenChange();
    }
  },

  /**
   * Store validation timestamp (for caching)
   */
  setValidationTimestamp(): void {
    localStorage.setItem(VALIDATION_TIMESTAMP_KEY, Date.now().toString());
  },

  /**
   * Check if validation cache is still valid (< 5 minutes old)
   */
  isValidationCacheValid(): boolean {
    const timestamp = localStorage.getItem(VALIDATION_TIMESTAMP_KEY);
    if (!timestamp) return false;
    const validatedAt = parseInt(timestamp, 10);
    return Date.now() - validatedAt < VALIDATION_CACHE_DURATION;
  },

  /**
   * Clear validation cache to force next validation to call Flask API
   */
  clearValidationCache(): void {
    localStorage.removeItem(VALIDATION_TIMESTAMP_KEY);
  },

  /**
   * Check if we can skip Flask API validation entirely
   * Returns true if:
   * 1. Token exists and is not expired (checked locally via JWT decode)
   * 2. Cached user exists
   * 3. Either cache is fresh OR token is still valid
   */
  canSkipValidation(): boolean {
    const token = this.getToken();
    const user = this.getUser();

    // Must have both token and cached user
    if (!token || !user) return false;

    // Check if token is still valid (not expired)
    if (!isTokenValid(token)) return false;

    // Token is valid - we can skip Flask API validation
    // (Only validate with Flask API when cache expires, for role/permission updates)
    return this.isValidationCacheValid();
  },

  /**
   * Attempt to refresh JWT token via Flask API
   * Only works if current token is valid and within refresh window (< 2 days until expiry)
   *
   * @returns New token if refreshed, null if refresh not needed or failed
   */
  async refreshToken(): Promise<string | null> {
    const token = this.getToken();
    if (!token || !shouldRefreshToken(token)) return null;

    // Never refresh impersonation tokens - they are intentionally short-lived (1 hour)
    if (getImpersonatorId(token) != null) return null;

    // Skip if offline
    if (!navigator.onLine) {
      console.log('[authService] Offline, skipping token refresh');
      return null;
    }

    try {
      const response = await api.post('/api/auth/refresh', {}, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = response.data;
      if (data.token) {
        this.setToken(data.token);
        this.setValidationTimestamp(); // Reset validation cache
        console.log('[authService] Token refreshed successfully');
        return data.token;
      }

      return null;
    } catch (error: unknown) {
      const authError = error as AuthApiError;
      // Handle 401 - token truly expired, trigger logout
      if (authError.response?.status === 401) {
        console.warn('[authService] Token expired during refresh, clearing auth');
        this.clearToken();
        return null;
      }
      console.warn('[authService] Token refresh network error:', authError);
      return null;
    }
  },

  /**
   * Store user data in localStorage (for quick access)
   */
  setUser(user: User): void {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  },

  /**
   * Get user data from localStorage
   */
  getUser(): User | null {
    const userJson = localStorage.getItem(USER_STORAGE_KEY);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson) as User;
    } catch {
      return null;
    }
  },

  /**
   * Validate JWT token via Flask API and fetch user data
   *
   * Calls Flask: GET /api/auth/me
   *
   * @param token - JWT token to validate
   * @returns User data if valid
   * @throws Error if token is invalid
   */
  async validateToken(token: string): Promise<User> {
    try {
      const response = await api.get('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = response.data;

      // Map Flask user response to our User type
      const user: User = {
        id: data.id,
        wp_user_id: data.wp_user_id ?? data.id,
        username: data.username || data.user_login || null,
        name: data.name ?? data.display_name ?? null,
        display_name: data.display_name ?? data.name ?? null,
        email: data.email ?? data.user_email ?? null,
        user_email: data.user_email ?? data.email ?? null,
        phone: data.phone ?? null,
        is_phone_verified: data.is_phone_verified ?? false,
        marketing_sms_consent_at: data.marketing_sms_consent_at ?? null,
        marketing_email_consent_at: data.marketing_email_consent_at ?? null,
        marketing_consent_answered_at: data.marketing_consent_answered_at ?? null,
        marketing_consent_deferred_until: data.marketing_consent_deferred_until ?? null,
        role: data.role || 'customer',
        is_premium: data.is_premium ?? false,
        created_at: data.created_at || new Date().toISOString(),
        last_login: new Date().toISOString(),
      };

      // Mark validation timestamp for caching
      this.setValidationTimestamp();

      return user;
    } catch (error) {
      console.error('[authService] Token validation error:', error);
      throw new Error('Invalid or expired token');
    }
  },

  /**
   * Login with token (stores token and returns user data)
   *
   * @param token - JWT token
   * @returns User data
   */
  async login(token: string): Promise<User> {
    // Validate token with backend
    const user = await this.validateToken(token);

    // Store token and user data
    this.setToken(token);
    this.setUser(user);

    return user;
  },

  /**
   * Login with username and password via Flask API
   *
   * Just fetches and stores the token. Validation will happen when
   * Login.tsx calls AuthContext.login(token).
   *
   * @param username - username or email
   * @param password - password
   */
  async loginWithPassword(username: string, password: string): Promise<void> {
    try {
      const response = await api.post('/api/auth/login', {
        username,
        password,
      }, {
        headers: {
          'X-Client-Platform': 'web',
        },
      });

      const data = response.data;
      const token = data.token;

      if (!token) {
        throw new Error('No token received');
      }

      // Just store the token (validation will happen in AuthContext.login())
      this.setToken(token);
      // No return value - Login.tsx calls login(token) for validation
    } catch (error: unknown) {
      const authError = error as AuthApiError;
      console.error('[authService] Password login error:', authError);
      // Re-throw with Flask error format (code + message)
      if (authError.response?.data) {
        const errData = authError.response.data;
        const err = new Error(errData.message || 'Login failed') as Error & { code?: string; status?: number };
        err.code = errData.code || errData.error || '';
        err.status = authError.response.status;
        throw err;
      }
      throw authError;
    }
  },

  /**
   * Logout user
   *
   * Clears token and user data from localStorage
   * Redirects to login page
   */
  logout(): void {
    this.clearToken();
    window.location.href = '/login';
  },

  /**
   * Fetch user credits from Flask API backend
   *
   * Uses the Flask backend /api/user/credits endpoint
   *
   * @param token - Optional JWT token (if not stored yet)
   * @returns Current credits balance
   */
  async fetchCredits(token?: string): Promise<number> {
    try {
      // If token provided, use it directly (for parallel fetching during login)
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const response = await api.get('/api/user/credits', { headers });
      const data = response.data as GetCreditsResponse;
      const credits = data?.credits;
      if (typeof credits !== 'number' || !Number.isFinite(credits)) {
        throw new Error('포인트 잔액을 확인할 수 없습니다.');
      }
      return credits;
    } catch (error) {
      console.warn('[authService] fetchCredits failed:', error);
      // AuthContext preserves the last known balance on refresh failure.
      throw error;
    }
  },

  /**
   * Check if user is authenticated (has valid, non-expired token)
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return isTokenValid(token);
  },

  /**
   * Extract token from URL parameters (Flask OAuth callback redirect)
   *
   * @param url - URL with token parameter
   * @returns Token if found, null otherwise
   */
  extractTokenFromUrl(url: string = window.location.href): string | null {
    const urlParams = new URLSearchParams(new URL(url).search);
    return urlParams.get('token');
  },

  /**
   * Extract token from cookie (alternative Flask OAuth redirect method)
   *
   * @param cookieName - Name of cookie containing token
   * @returns Token if found, null otherwise
   */
  extractTokenFromCookie(cookieName: string = 'autoqm_token'): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${cookieName}=`);

    if (parts.length === 2) {
      const token = parts.pop()?.split(';').shift();
      return token || null;
    }

    return null;
  },

  /**
   * Initiate Social Login (Google, Naver, Kakao)
   *
   * Redirects to Flask social login endpoint
   * After successful OAuth, Flask will redirect back with ?token= in URL
   *
   * @param provider - Social login provider: 'google' | 'naver' | 'kakao'
   */
  initiateSocialLogin(
    provider: 'google' | 'naver' | 'kakao',
    options?: { redirect?: string | null }
  ): void {
    const path = buildSocialLoginPath(provider, options?.redirect);

    // Google blocks OAuth in in-app browsers (403 disallowed_useragent).
    // Other providers (Naver, Kakao) work fine in their own in-app browsers.
    if (provider === 'google') {
      const ua = navigator.userAgent || '';
      const isInApp = /KAKAOTALK|NAVER|Line\/|Instagram|FBAN|FBAV|SamsungBrowser\/(?!.*Chrome)/i.test(ua);

      if (isInApp) {
        const fullUrl = `${window.location.origin}${path}`;

        if (/Android/i.test(ua)) {
          window.location.href =
            `intent://${fullUrl.replace(/https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
          return;
        }

        if (/iPhone|iPad|iPod/i.test(ua)) {
          if (/KAKAOTALK/i.test(ua)) {
            window.location.href =
              `kakaotalk://web/openExternal?url=${encodeURIComponent(fullUrl)}`;
            return;
          }
          window.location.href =
            `googlechromes://${fullUrl.replace(/https?:\/\//, '')}`;
          return;
        }

        window.open(fullUrl, '_system');
        return;
      }
    }

    window.location.href = path;
  },
};

export default authService;
